import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { usePatchSubjectSettingsMutation, useSalesRepsQuery } from '@/client/api/sales/sales';
import type {
  SalesConsentSource,
  SalesConversationChannel,
  SalesSubject,
  SalesSubjectSettings,
} from '@/client/types/sales';
import { Panel, btnPrimary, selectCls, toastSales } from './sales-ui';

const CHANNELS: { id: SalesConversationChannel; label: string }[] = [
  { id: 'whatsapp', label: 'WhatsApp' },
  { id: 'sms', label: 'SMS' },
  { id: 'email', label: 'Email' },
];
const SOURCES: { id: SalesConsentSource; label: string }[] = [
  { id: 'call', label: 'On a call' },
  { id: 'form', label: 'A form' },
  { id: 'in_person', label: 'In person' },
  { id: 'message', label: 'By message' },
  { id: 'email', label: 'By email' },
  { id: 'other', label: 'Other' },
];
const inputCls = 'rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-sm text-gray-800';

/**
 * How to reach this prospect. Preferred channel, contact hours and where
 * consent came from can be set by anyone who edits prospects; their own caps,
 * spacing and a pin to one rep need the Sales settings permission.
 */
export function SubjectSettingsPanel({ subject, canManage }: { subject: SalesSubject; canManage: boolean }) {
  const save = usePatchSubjectSettingsMutation();
  const reps = useSalesRepsQuery().data || [];
  const [draft, setDraft] = useState<SalesSubjectSettings>(subject.settings || {});
  useEffect(() => setDraft(subject.settings || {}), [subject.settings]);

  const submit = () => {
    const patch: Record<string, unknown> = {
      preferredChannel: draft.preferredChannel || null,
      contactHours: draft.contactHours?.start && draft.contactHours?.end ? draft.contactHours : null,
      consentSource: draft.consentSource && Object.keys(draft.consentSource).length ? draft.consentSource : null,
    };
    if (canManage) {
      patch.pinnedRepId = draft.pinnedRepId || null;
      patch.channelCaps = draft.channelCaps && Object.keys(draft.channelCaps).length ? draft.channelCaps : null;
      patch.outreachMinHours =
        draft.outreachMinHours && Object.keys(draft.outreachMinHours).length ? draft.outreachMinHours : null;
    }
    save.mutate(
      { subjectId: subject.id, patch },
      { onSuccess: () => toast.success('Contact settings saved'), onError: toastSales },
    );
  };

  const setSource = (c: SalesConversationChannel, source: string) => {
    const next = { ...draft.consentSource };
    if (source) next[c] = { ...next[c], source: source as SalesConsentSource };
    else delete next[c];
    setDraft({ ...draft, consentSource: next });
  };
  const setCap = (c: SalesConversationChannel, key: 'max' | 'days', value: string) => {
    const next = { ...draft.channelCaps };
    const cur = next[c] || { max: 0, days: 7 };
    if (value === '' && key === 'max') delete next[c];
    else next[c] = { ...cur, [key]: Number(value) };
    setDraft({ ...draft, channelCaps: next });
  };
  const setSpacing = (c: SalesConversationChannel, value: string) => {
    const next = { ...draft.outreachMinHours };
    if (value === '') delete next[c];
    else next[c] = Number(value);
    setDraft({ ...draft, outreachMinHours: next });
  };

  return (
    <Panel
      title="Contact settings"
      className="mb-4"
      actions={
        <button type="button" className={btnPrimary} disabled={save.isPending} onClick={submit}>
          Save
        </button>
      }
    >
      <div className="grid gap-5 md:grid-cols-2">
        <label className="block text-sm">
          <span className="font-medium text-gray-900">Preferred channel</span>
          <select
            className={`${selectCls} mt-1 block w-full`}
            value={draft.preferredChannel || ''}
            onChange={(e) => setDraft({ ...draft, preferredChannel: (e.target.value || undefined) as SalesConversationChannel })}
          >
            <option value="">No preference</option>
            {CHANNELS.map((c) => (
              <option key={c.id} value={c.id}>
                {c.label}
              </option>
            ))}
          </select>
        </label>

        <fieldset className="text-sm">
          <legend className="font-medium text-gray-900">Best time to contact (Accra)</legend>
          <div className="mt-1 flex items-center gap-2 text-gray-700">
            <input
              type="time"
              aria-label="Contact from"
              className={inputCls}
              value={draft.contactHours?.start || ''}
              onChange={(e) => setDraft({ ...draft, contactHours: { start: e.target.value, end: draft.contactHours?.end || '' } })}
            />
            to
            <input
              type="time"
              aria-label="Contact until"
              className={inputCls}
              value={draft.contactHours?.end || ''}
              onChange={(e) => setDraft({ ...draft, contactHours: { start: draft.contactHours?.start || '', end: e.target.value } })}
            />
          </div>
          <p className="mt-1 text-xs text-gray-500">The composer warns outside these hours; it does not block.</p>
        </fieldset>

        <fieldset className="text-sm md:col-span-2">
          <legend className="font-medium text-gray-900">Where consent came from</legend>
          <div className="mt-1 grid gap-2 md:grid-cols-3">
            {CHANNELS.map((c) => (
              <div key={c.id}>
                <label className="block text-xs text-gray-600">
                  {c.label}
                  <select
                    className={`${selectCls} mt-1 block w-full`}
                    value={draft.consentSource?.[c.id]?.source || ''}
                    onChange={(e) => setSource(c.id, e.target.value)}
                  >
                    <option value="">Not recorded</option>
                    {SOURCES.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.label}
                      </option>
                    ))}
                  </select>
                </label>
                {draft.consentSource?.[c.id] && (
                  <input
                    type="text"
                    aria-label={`${c.label} consent note`}
                    placeholder="Note, e.g. agreed on the 12 Oct call"
                    className={`${inputCls} mt-1 w-full`}
                    value={draft.consentSource[c.id]?.note || ''}
                    onChange={(e) =>
                      setDraft({
                        ...draft,
                        consentSource: { ...draft.consentSource, [c.id]: { ...draft.consentSource![c.id]!, note: e.target.value } },
                      })
                    }
                  />
                )}
              </div>
            ))}
          </div>
          <p className="mt-1 text-xs text-gray-500">Recording a source also gives consent for that channel. To withdraw it, untick consent in the profile.</p>
        </fieldset>

        {canManage && (
          <>
            <label className="block text-sm">
              <span className="font-medium text-gray-900">Pin to one rep</span>
              <select
                className={`${selectCls} mt-1 block w-full`}
                value={draft.pinnedRepId || ''}
                onChange={(e) => setDraft({ ...draft, pinnedRepId: e.target.value || undefined })}
              >
                <option value="">Anyone may claim</option>
                {reps.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.name}
                  </option>
                ))}
              </select>
              <span className="mt-1 block text-xs text-gray-500">Only they can claim or message this prospect.</span>
            </label>

            <fieldset className="text-sm md:col-span-2">
              <legend className="font-medium text-gray-900">Own caps and spacing</legend>
              <p className="mb-2 text-xs text-gray-500">Blank means the floor's rule. A cap of 0 stops that channel for this person.</p>
              <div className="grid gap-3 md:grid-cols-3">
                {CHANNELS.map((c) => (
                  <div key={c.id} className="space-y-1 text-xs text-gray-600">
                    <p className="font-medium text-gray-800">{c.label}</p>
                    <label className="flex items-center gap-2">
                      Max
                      <input
                        type="number"
                        min={0}
                        className={`${inputCls} w-16`}
                        value={draft.channelCaps?.[c.id]?.max ?? ''}
                        onChange={(e) => setCap(c.id, 'max', e.target.value)}
                      />
                      per
                      <input
                        type="number"
                        min={1}
                        className={`${inputCls} w-16`}
                        disabled={!draft.channelCaps?.[c.id]}
                        value={draft.channelCaps?.[c.id]?.days ?? ''}
                        onChange={(e) => setCap(c.id, 'days', e.target.value)}
                      />
                      days
                    </label>
                    <label className="flex items-center gap-2">
                      At least
                      <input
                        type="number"
                        min={0}
                        className={`${inputCls} w-16`}
                        value={draft.outreachMinHours?.[c.id] ?? ''}
                        onChange={(e) => setSpacing(c.id, e.target.value)}
                      />
                      hours apart
                    </label>
                  </div>
                ))}
              </div>
            </fieldset>
          </>
        )}
      </div>
    </Panel>
  );
}
